import {
  getLightProductDefinition,
  type C1_LightProductId,
  type C1_LightUsageCategory,
} from "../data/C1_ProductDefinitions";
import { newId } from "../../../src/models/BaseObject";
import { ChannelType, type C1_EloItem_Data } from "../types/C1_EloTypes";
import type { C1_FurnitureProps } from "../types/C1_FurnitureTypes";

export const LIGHT_GROUP_DEFINITION_ID = "lightGroup";
export const LIGHT_GROUP_LIGHT_DEFINITION_ID = "lightGroup.light";
export const OUTDOOR_LIGHT_GROUP_DEFINITION_ID = "outdoorLightGroup";
export const OUTDOOR_LIGHT_GROUP_LIGHT_DEFINITION_ID = "outdoorLightGroup.light";
export const LIGHT_GROUP_ELO_DEFINITION_IDS = {
  ceiling: "lightGroup.ceiling",
  wall: "lightGroup.wall",
  outdoorWall: "outdoorLightGroup.wall",
  outdoorFloor: "outdoorLightGroup.floor",
  outdoorCeiling: "outdoorLightGroup.ceiling",
} as const;
function supportsUsage(productId: unknown, usage: C1_LightUsageCategory): productId is C1_LightProductId {
  return typeof productId === "string" && getLightProductDefinition(productId)?.usage.includes(usage) === true;
}

export function isLightGroupDefinitionId(id: string): boolean {
  return id === LIGHT_GROUP_DEFINITION_ID || id === OUTDOOR_LIGHT_GROUP_DEFINITION_ID;
}

export function isSingleLightDefinitionId(id: string): boolean {
  return id === "electricalLight" || id === "outdoorLight";
}

export function lightGroupCount(value: unknown): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.min(100, Math.max(1, Math.floor(numericValue))) : 3;
}

export function lightGroupRows(value: unknown): number {
  return Math.min(100, Math.max(1, Math.floor(Number(value) || 1)));
}

function productForMounting(productId: unknown, wallDocked: boolean, outdoor: boolean): C1_LightProductId {
  if (outdoor) {
    const currentId = typeof productId === "string" ? (productId as C1_LightProductId) : undefined;
    if (wallDocked) {
      return supportsUsage(currentId, "outdoorWall") ? currentId : "outdoor-spot-single";
    }
    if (supportsUsage(currentId, "outdoorCeiling")) return currentId;
    return supportsUsage(currentId, "outdoorFloor") ? currentId : "outdoor-stand-bollard";
  }
  const current = typeof productId === "string" ? getLightProductDefinition(productId) : undefined;
  const expectedUsage = wallDocked ? "indoorWall" : "indoorCeiling";
  const expectedSymbol = wallDocked ? "symbol-light-wall" : "symbol-light-ceiling";
  return current?.usage.includes(expectedUsage) || productId === "symbol-light" || productId === expectedSymbol
    ? (productId as C1_LightProductId)
    : expectedSymbol;
}

function eloDefinitionForMounting(outdoor: boolean, wallDocked: boolean, lightProductId: C1_LightProductId): string {
  if (!outdoor) return wallDocked ? LIGHT_GROUP_ELO_DEFINITION_IDS.wall : LIGHT_GROUP_ELO_DEFINITION_IDS.ceiling;
  if (wallDocked) return LIGHT_GROUP_ELO_DEFINITION_IDS.outdoorWall;
  return getLightProductDefinition(lightProductId)?.usage.includes("outdoorCeiling")
    ? LIGHT_GROUP_ELO_DEFINITION_IDS.outdoorCeiling
    : LIGHT_GROUP_ELO_DEFINITION_IDS.outdoorFloor;
}

export function syncLightGroup(
  props: C1_FurnitureProps,
  eloTree: C1_EloItem_Data | undefined,
  wallDocked: boolean,
  definitionId = LIGHT_GROUP_DEFINITION_ID,
): { props: C1_FurnitureProps; eloTree: C1_EloItem_Data } {
  const existingRoot = eloTree;
  const outdoor =
    definitionId === OUTDOOR_LIGHT_GROUP_DEFINITION_ID ||
    existingRoot?.definitionId === OUTDOOR_LIGHT_GROUP_DEFINITION_ID ||
    existingRoot?.definitionId.startsWith(`${OUTDOOR_LIGHT_GROUP_DEFINITION_ID}.`) === true;
  const count = lightGroupCount(props.count);
  const rows = lightGroupRows(props.rows);
  const effectiveRows = wallDocked ? 1 : rows;
  const selectedChannelType = props.channelType ?? existingRoot?.items[0]?.details?.channelType;
  const channelType = Object.values(ChannelType).includes(selectedChannelType)
    ? (selectedChannelType as ChannelType)
    : outdoor
      ? ChannelType.Switch
      : ChannelType.Dimmer;
  const lightProductId = productForMounting(props.lightProductId, wallDocked, outdoor);
  const eloDefinitionId = eloDefinitionForMounting(outdoor, wallDocked, lightProductId);
  const lightDefinitionId = `${eloDefinitionId}.light`;
  const existingLight = existingRoot?.items[0];
  const light: C1_EloItem_Data = {
    ...(existingLight ?? { id: newId(), definitionId: lightDefinitionId, items: [] }),
    definitionId: lightDefinitionId,
    name: "Leuchtengruppe",
    position: { x: 0, y: 0 },
    details: { ...existingLight?.details, channelType, isDali: props.isDali ?? existingLight?.details?.isDali, lightProductId },
  };

  return {
    props: { ...props, count, rows, effectiveRows, channelType, lightProductId },
    eloTree: {
      id: existingRoot?.id ?? newId(),
      definitionId: eloDefinitionId,
      items: [light],
    },
  };
}

export function syncSingleLight(
  props: C1_FurnitureProps,
  eloTree: C1_EloItem_Data | undefined,
  wallDocked: boolean,
  furnitureDefinitionId: string,
): { props: C1_FurnitureProps; eloTree: C1_EloItem_Data } {
  const root = eloTree;
  const existingLight = root?.items[0];
  const outdoor = furnitureDefinitionId === "outdoorLight";
  const lightProductId = productForMounting(existingLight?.details?.lightProductId, wallDocked, outdoor);
  const mountingDefinitionId = eloDefinitionForMounting(outdoor, wallDocked, lightProductId)
    .replace("lightGroup.", "electricalLight.")
    .replace("outdoorLightGroup.", "outdoorLight.");
  const light: C1_EloItem_Data = {
    ...(existingLight ?? { id: newId(), items: [] }),
    definitionId: mountingDefinitionId,
    details: { ...existingLight?.details, lightProductId },
  };
  return {
    props: { ...props },
    eloTree: {
      id: root?.id ?? newId(),
      definitionId: furnitureDefinitionId,
      items: [light],
    },
  };
}
